import { useMemo } from "react";

import { Flex, NumberInput } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";

import { QuestionnaireDecimalFieldProps } from "../QuestionnaireDecimalField";

export default function QuestionnaireNumberInputField<T>({
  placeholder,
  rightSection,
  error,
  form,
  label,
  withAsterisk,
  minRange,
  maxRange,
  minValue,
  maxValue,
  maxDecimalPlaces,
  allowDecimal = true,
  allowNegative = true,
  showPlusPrefix = false,
  value,
  fieldPath,
  readOnly = false,
  "data-testid": dataTestId,
}: Omit<QuestionnaireDecimalFieldProps<T>, "key"> & {
  fieldPath: string;
  readOnly?: boolean;
  "data-testid"?: string;
}) {
  const rightSectionWidth = useMemo(() => {
    if (!rightSection) return undefined;
    const minWidth = 70;
    const charWidth = 8;
    return Math.max(minWidth, rightSection.length * charWidth + 32);
  }, [rightSection]);

  const resolvedMin = minRange ?? minValue;
  const resolvedMax = maxRange ?? maxValue;

  const prefix = showPlusPrefix && typeof value === "number" && value > 0 ? "+" : undefined;

  return (
    <NumberInput
      {...form.getInputProps(fieldPath)}
      value={value}
      error={error}
      label={label}
      prefix={prefix}
      min={resolvedMin}
      max={resolvedMax}
      readOnly={readOnly}
      clampBehavior="strict"
      required={withAsterisk}
      data-testid={dataTestId}
      placeholder={placeholder}
      allowDecimal={allowDecimal}
      allowNegative={allowNegative}
      decimalScale={maxDecimalPlaces}
      rightSectionWidth={rightSectionWidth}
      onChange={(val: string | number) => {
        const newValue = val === "" ? "" : Number(val);
        setFormFieldValue(form, fieldPath, newValue);
      }}
      rightSection={
        rightSection ? (
          <Flex
            px="8px"
            h="100%"
            w="100%"
            bg="gray.1"
            ta="center"
            align="center"
            justify="center"
            style={{
              borderLeft: "1px solid var(--mantine-color-gray-4)",
              borderRadius: "0 4px 4px 0",
            }}
          >
            <TextWrapper c="gray.7" variant="bold">
              {rightSection}
            </TextWrapper>
          </Flex>
        ) : undefined
      }
    />
  );
}
